import React, { forwardRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import styles from './PhoneInput.module.scss';

interface PhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ label, error, className, value, onChange, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState('');

    // Strips formatting, removes +90 prefix, keeps only digits and '*', caps at 10 chars
    const normalizePhone = (value: string): string => {
      let clean = value.replace(/[^\d*]/g, '');
      if (clean.startsWith('90')) {
        clean = clean.slice(2);
      }
      return clean.slice(0, 10);
    };

    const formatPhoneNumber = (value: string) => {
      if (!value) return '';
      const cleanValue = normalizePhone(value);
      const char: Record<number, string> = { 0: '(', 3: ') ', 6: ' ', 8: ' ' };
      let res = '+90 ';
      for (let i = 0; i < cleanValue.length; i++) {
        res += (char[i] || '') + cleanValue[i];
      }
      return res;
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleanValue = normalizePhone(e.target.value);
      const formatted = formatPhoneNumber(cleanValue);
      setDisplayValue(formatted);
      if (onChange) {
        // @ts-ignore
        onChange({ target: { value: cleanValue ? '90' + cleanValue : '', name: props.name } });
      }
    };

    useEffect(() => {
        const normalized = normalizePhone(value ? String(value) : '');
        setDisplayValue(formatPhoneNumber(normalized));
    }, [value]);

    return (
      <div className={styles.container}>
        {label && <label className={styles.label}>{label}</label>}
        <div className={styles.inputWrapper}>
          <input
            ref={ref}
            type="tel"
            className={clsx(styles.input, { [styles.hasError]: error }, className)}
            value={displayValue}
            onChange={handleInput}
            placeholder="+90 (5XX) XXX XX XX"
            {...props}
          />
        </div>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
